import {Platform} from "@variocube/app-api";
import {EventSupportingApp, occupyAndConfirm} from "@variocube/app-common";
import {AppContext, AppErrorBoundary} from "@variocube/cube-ui";
import {DateTime} from "luxon";
import * as React from "react";
import {render} from "react-dom";
import {LocalizationProvider, SUPPORTED_LANGUAGES} from "../i18n";
import {Availability, BookingOptions, BookingPlan, Bookings, OccupancyBooking} from "./booking/booking";
import icon from "./lock-icon.svg";
import {SignUpUrlKey, UtilizationKey} from "./model";
import {SafeCubeShell} from "./SafeCubeShell";

export default class extends EventSupportingApp {
	constructor(platform: Platform) {
		super(platform, icon, "#f08721", SUPPORTED_LANGUAGES, true);
	}

	render(appRoot: Element) {
		const appContext = {
			platform: this.platform,
			appEventSupport: this.eventSupport,
		};
		render(
			<AppContext.Provider value={appContext}>
				<AppErrorBoundary appId={this.platform.appId} cubeId={this.platform.hostname}>
					<LocalizationProvider app={this}>
						<SafeCubeShell />
					</LocalizationProvider>
				</AppErrorBoundary>
			</AppContext.Provider>,
			appRoot,
		);
	}

	async loadMockData() {
		// args for testing custom branding
		const colorPrimary = this.platform.getArg("colorPrimary");
		const theme = colorPrimary ? {colorPrimary} : undefined;
		const background = this.platform.getArg("background");

		let backgroundImage = undefined;
		if (background) {
			const backgroundResponse = await window.fetch(
				"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mPccePjfwAIUwOCOf5cIwAAAABJRU5ErkJggg==",
			);
			backgroundImage = await backgroundResponse.blob();
		}

		const bookingDateToday = DateTime.local();
		const bookingDateYesterday = DateTime.local().minus({days: 2});
		const bookingDateThreeDaysAgo = DateTime.local().minus({days: 3});

		const occupancyBooking = {
			cardUid: "NDIK", // card uid from MockNfcReader
			units: 1,
			boxType: "M",
			amount: 1.00,
			phone: "1234",
			from: bookingDateToday.startOf("day").toISO(),
			until: bookingDateToday.endOf("day").toISO(),
		} as OccupancyBooking;

		const occupancyContent = {
			// when the backend creates an occupancy it writes the occupancyId
			occupancyId: 7,
			// when booking on the cube, we write this booking object
			booking: occupancyBooking,
		};

		// normal occupancy
		await occupyAndConfirm(this.platform, {
			accessCode: "1234567",
			type: "M",
			content: {
				...occupancyContent,
			},
		});

		// released occupancy
		await occupyAndConfirm(this.platform, {
			accessCode: "2",
			type: "M",
			content: {
				...occupancyContent,
				// set a release date for this occupancy
				released: DateTime.local().toISO(),
			},
		});

		// booking ended
		await occupyAndConfirm(this.platform, {
			accessCode: "3",
			type: "M",
			content: {
				...occupancyContent,
				booking: {
					...occupancyBooking,
					from: bookingDateThreeDaysAgo.startOf("day").toISO(),
					until: bookingDateThreeDaysAgo.endOf("day").toISO(),
				},
			},
		});

		return {
			[UtilizationKey]: {
				load: 5,
				capacity: 10,
			},
			[SignUpUrlKey]: "https://admin.safecube.io/api/signup",
			// verifyPhoneUrl: "https://admin-dev.safecube.io/api/phone-verification",
			// verifyEmailUrl: "https://admin-dev.safecube.io/api/email-verification",

			bookingOptions: {
				enableBooking: true,
				enablePayment: {
					maxAmount: 20,
				},
				enableEmail: true,
				enablePhone: true,
				enableDisplay: true,
				enableBookingCode: true, // whether booking code can be entered
				accessibleFrom: this.platform.getArg("accessibleFrom") || "08:00",
				accessibleUntil: this.platform.getArg("accessibleUntil") || "22:00",
				accessibility: {
					Monday: {from: "23:00", until: "22:51"},
					Tuesday: {from: "23:00", until: "22:52"},
					Wednesday: {from: "23:00", until: "22:53"},
					Thursday: {from: "23:00", until: "22:54"},
					Friday: {from: "23:00", until: "22:55"},
					Saturday: {from: "23:00", until: "22:56"},
					Sunday: {from: "23:00", until: "22:57"},
				},
				/* accessibility: {
                    Monday: {from: "08:00", until: "22:51"},
                    Tuesday: {from: "08:00", until: "22:52"},
                    Wednesday: {from: "08:00", until: "22:53"},
                    Thursday: {from: "08:00", until: "22:54"},
                    Friday: {from: "08:00", until: "22:55"},
                    Saturday: {from: "08:00", until: "22:56"},
                    Sunday: {from: "08:00", until: "22:57"},
                },*/
				terms: "https://my.terms/terms.pdf",
				termsUrl: "https://variocube.com/terms.pdf",
				privacyPolicyUrl: "https://variocube.com/privacy.pdf",
				bookingUnit: this.platform.getArg("bookingUnit") || "Days",
				pricingMode: this.platform.getArg("pricingMode") || "Unit",
				plans: getMockPlans(this.platform.getArg("plans")),
				currency: "USD",
				siteId: 42,
				siteName: "Mock site",
				siteCubes: [
					"localhost",
					"other.variocube.local",
				],
			} as BookingOptions,
			availability: {
				"XS": {
					max: 10,
				},
				"S": {
					max: 24,
				},
				"M": {
					max: 0,
				},
				"NA": {
					max: 1,
				},
				"L": {
					max: 24,
				},
				"S-Charger": {
					max: 100,
				},
				"Ski": {
					max: 5,
				},
			} as Availability,
			bookings: {
				"9999": {
					uuid: "d2c43b88-6a4a-45dc-beec-f85c17afe36c",
					bookingCode: "9999",
					boxes: {
						S: 9,
						M: 3,
					},
					from: "2020-02-11T00:00:00Z",
					until: "2099-02-11T23:59:59Z",
					name: "John",
					email: "john@doe.local",
					phone: "",
					occupancies: [{
						boxNumber: "24",
						ended: "1999-12-31T08:00:00Z",
						cube: this.platform.hostname,
						occupancy: "very-random-such-unique-id",
						boxType: "S",
					}],
					state: "Confirmed",
				},
				// booking with ended occupancy that should reclaim the same box
				"99999": {
					uuid: "99999b88-6a4a-45dc-beec-f85c17afe36c",
					bookingCode: "99999",
					boxes: {
						S: 1,
					},
					from: "2020-02-11T00:00:00Z",
					until: "2099-02-11T23:59:59Z",
					name: "John",
					email: "john@doe.local",
					phone: "",
					occupancies: [{
						boxNumber: "1",
						ended: "1999-12-31T08:00:00Z",
						cube: this.platform.hostname,
						occupancy: "very-random-such-unique-id",
						boxType: "S",
					}],
					state: "Confirmed",
				},
				"future": {
					uuid: "d2c43b88-6a4a-45dc-beec-f85c17afe36c",
					bookingCode: "future",
					bookingKey: "secret",
					boxes: {
						S: 1,
					},
					from: "2099-02-11T00:00:00Z",
					until: "2099-02-11T23:59:59Z",
					name: "John",
					email: "john@doe.local",
					phone: "",
					occupancies: [],
					state: "Confirmed",
				},
				"neighbor": {
					uuid: "d2c43b88-6a4a-45dc-beec-f85c17afe36c",
					bookingCode: "neighbor",
					boxes: {
						M: 2,
					},
					from: "2000-02-11T00:00:00Z",
					until: "2099-02-11T23:59:59Z",
					name: "John",
					email: "john@doe.local",
					phone: "",
					occupancies: [{
						cube: "other001.variocube.local",
						boxNumber: "77",
						boxType: "M",
						occupancy: "very-random-u-u-id",
					}],
					state: "Confirmed",
				},
				"empty": {
					uuid: "d2c43b88-6a4a-45dc-beec-f85c17afe36c",
					bookingCode: "empty",
					boxes: {
						M: 1,
					},
					from: "2000-02-11T00:00:00Z",
					until: "2099-02-11T23:59:59Z",
					name: "John",
					email: "john@doe.local",
					phone: "",
					occupancies: [{
						cube: "other001.variocube.local",
						boxNumber: "77",
						boxType: "M",
						occupancy: "very-random-u-u-id",
					}],
					state: "Confirmed",
				},
			} as Bookings,
			theme,
			backgroundImage,
		};
	}
}

const TwoPlans: BookingPlan[] = [
	{
		boxType: "L",
		units: [
			{from: 0, price: 1},
			{from: 6, price: 0.8},
			{from: 12, price: 0.6},
			{from: 16, price: 2},
		],
		packages: [
			{quantity: 6, price: 5},
			{quantity: 12, price: 7},
			{quantity: 24, price: 9},
		],
	},
	{
		boxType: "M",
		units: [
			{from: 0, price: 1},
			{from: 6, price: 0.8},
			{from: 12, price: 0.6},
			{from: 16, price: 2},
		],
		packages: [
			{quantity: 6, price: 3},
			{quantity: 12, price: 5},
			{quantity: 24, price: 7},
		],
	},
];

const ThreePlans = [
	...TwoPlans,
	{
		boxType: "XS",
		units: [
			{from: 0, price: 1},
			{from: 6, price: 0.8},
			{from: 12, price: 0.6},
			{from: 16, price: 2},
		],
		packages: [
			{quantity: 6, price: 3},
			{quantity: 12, price: 5},
			{quantity: 24, price: 7},
		],
	},
];

const FourPlans: BookingPlan[] = [
	...ThreePlans,
	{
		boxType: "XL",
		units: [
			{from: 0, price: 2},
			{from: 6, price: 1.8},
			{from: 12, price: 1.6},
			{from: 16, price: 6},
		],
		packages: [
			{quantity: 6, price: 7},
			{quantity: 12, price: 9},
			{quantity: 24, price: 12},
		],
	},
];

function getMockPlans(plan: string | undefined): BookingPlan[] {
	switch (plan) {
		case "2": // shows 2 plans
			return TwoPlans;

		case "3": // shows 3 plans
			return ThreePlans;

		case "4": // shows 4 plans
			return FourPlans;

		case "schluessfach": // shows plans for Schlüssfach
			return [
				{
					boxType: "M",
					units: [{from: 0, price: 10}],
					packages: [],
					dimensions: {width: 400, height: 180, depth: 350},
				},
				{
					boxType: "L",
					units: [{from: 0, price: 15}],
					packages: [],
					dimensions: {width: 400, height: 360, depth: 350},
				},
			];

		case "rathausgalerien": // shows plans for Rathausgalerien
			return [
				...FourPlans,
				{
					boxType: "S-Charger",
					units: [{from: 0, price: 2.5}],
					packages: [],
				},
				{
					boxType: "Ski",
					units: [{from: 0, price: 7.5}],
					packages: [],
				},
			];

		case "na": // includes a non-available box type
			return [
				...ThreePlans,
				{
					boxType: "NA",
					units: [{from: 0, price: 2.5}],
					packages: [],
				},
			];

		default:
			return ThreePlans;
	}
}
